// src/pages/NewsItemPublicPage.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BlocksRenderer } from '@strapi/blocks-react-renderer';

import Header from '../components/Header';
import Footer from '../components/Footer';
import LoginModal from '../components/LoginModal';
import RegistrationModal from '../components/RegistrationModal';
import InfoModal from '../components/InfoModal';
import { ReactComponent as ChevronUpIcon } from '../assets/icon-chevron-up.svg';
import SEO from '../components/SEO';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;

function NewsItemPublicPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [newsItem, setNewsItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Alleen bij een echte 'bestaat niet' krijgt de pagina noindex. Bij een tijdelijke
  // laadfout niet: Google zou de pagina dan uit de index halen.
  const [notFound, setNotFound] = useState(false);

  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [privacyData, setPrivacyData] = useState(null);
  const [contactData, setContactData] = useState(null);

  useEffect(() => {
    const fetchNewsItem = async () => {
      setLoading(true);
      setError(null);
      setNotFound(false);
      try {
        const url = `${STRAPI_URL}/api/nieuws-items?filters[slug][$eq]=${slug}&populate[0]=featured_image&populate[1]=auteurs.photo&populate[2]=related_doorloopjes.steps.image&populate[3]=related_digidates.image`;
        const response = await axios.get(url);

        if (response.data?.data?.length > 0) {
          const rawData = response.data.data[0];
          setNewsItem(rawData);
        } else {
          setError('Dit nieuwsbericht bestaat niet of is verplaatst.');
          setNotFound(true);
        }
      } catch (err) {
        console.error('Fout bij het ophalen van het nieuwsitem:', err);
        setError('Dit nieuwsbericht laden lukte niet. Probeer het opnieuw.');
      } finally {
        setLoading(false);
      }
    };

    fetchNewsItem();
  }, [slug]);

  useEffect(() => {
    // Fetch privacy and contact data for footer
    const fetchFooterData = async () => {
      try {
        const [privacyRes, contactRes] = await Promise.all([
          axios.get(`${STRAPI_URL}/api/privacyverklaring?populate=*`),
          axios.get(`${STRAPI_URL}/api/contact-pagina?populate=*`)
        ]);
        setPrivacyData(privacyRes.data.data);
        setContactData(contactRes.data.data);
      } catch (err) {
        console.error('Fout bij het ophalen van footer data:', err);
      }
    };

    fetchFooterData();
  }, []);

  const handleLoginClick = () => {
    setIsLoginModalOpen(true);
  };

  const handleRegisterClick = () => {
    setIsRegisterModalOpen(true);
  };

  if (loading) {
    return (
      <div className="page-container seo-page">
        <Header
          onLoginClick={handleLoginClick}
          onRegisterClick={handleRegisterClick}
        />
        <main>
          <p>Nieuwsbericht wordt geladen...</p>
        </main>
        <Footer
          onLoginClick={handleLoginClick}
          onPrivacyClick={() => setIsPrivacyModalOpen(true)}
          onContactClick={() => setIsContactModalOpen(true)}
        />
      </div>
    );
  }

  if (error || !newsItem) {
    return (
      <div className="page-container seo-page">
        <SEO
          title="Nieuwsbericht niet gevonden | Doorloopjes"
          description="Dit nieuwsbericht bestaat niet of is verplaatst."
          noindex={notFound}
        />
        <Header
          onLoginClick={handleLoginClick}
          onRegisterClick={handleRegisterClick}
        />
        <main>
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <h1>Nieuwsbericht niet gevonden</h1>
            <p>{error || 'Dit nieuwsbericht bestaat niet of is verplaatst.'}</p>
            <button onClick={() => navigate('/')} className="btn-primary" style={{ marginTop: '1rem' }}>
              Terug naar home
            </button>
          </div>
        </main>
        <Footer
          onLoginClick={handleLoginClick}
          onPrivacyClick={() => setIsPrivacyModalOpen(true)}
          onContactClick={() => setIsContactModalOpen(true)}
        />
      </div>
    );
  }

  const imageUrl = newsItem.featured_image?.url || null;

  // Extract description from summary or first 150 chars of body
  const getDescription = () => {
    if (newsItem.summary) return newsItem.summary;
    if (newsItem.body && Array.isArray(newsItem.body) && newsItem.body[0]?.children?.[0]?.text) {
      const text = newsItem.body[0].children[0].text;
      return text.length > 150 ? text.substring(0, 150) + '...' : text;
    }
    return 'Lees dit nieuwsbericht op Doorloopjes';
  };

  // Structured Data for NewsArticle
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: newsItem.title,
    description: getDescription(),
    image: imageUrl,
    datePublished: newsItem.publication_date || newsItem.createdAt,
    dateModified: newsItem.updatedAt,
    author: newsItem.auteurs?.map(author => ({
      '@type': 'Person',
      name: author.name,
      jobTitle: author.title
    })) || [],
    publisher: {
      '@type': 'Organization',
      name: 'Doorloopjes',
      logo: {
        '@type': 'ImageObject',
        url: `${window.location.origin}/images/logo.png`
      }
    }
  };

  return (
    <div className="page-container seo-page">
      <SEO
        title={`${newsItem.title} | Doorloopjes`}
        description={getDescription()}
        image={imageUrl}
        url={`/nieuws/${slug}`}
        type="article"
        structuredData={structuredData}
      />
      <Header
        onLoginClick={handleLoginClick}
        onRegisterClick={handleRegisterClick}
      />

      {isLoginModalOpen && (
        <LoginModal
          onClose={() => setIsLoginModalOpen(false)}
          onLoginSuccess={() => {
            setIsLoginModalOpen(false);
            window.location.href = '/dashboard';
          }}
          onOpenRegister={() => {
            setIsLoginModalOpen(false);
            setIsRegisterModalOpen(true);
          }}
        />
      )}

      {isRegisterModalOpen && (
        <RegistrationModal
          onClose={() => setIsRegisterModalOpen(false)}
          onRegisterSuccess={() => {
            setIsRegisterModalOpen(false);
            window.location.href = '/dashboard';
          }}
        />
      )}

      <InfoModal
        show={isPrivacyModalOpen}
        onClose={() => setIsPrivacyModalOpen(false)}
        title={privacyData?.title}
        content={privacyData?.body}
        loading={false}
        showSocial={false}
      />

      <InfoModal
        show={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        title={contactData?.title}
        content={contactData?.body}
        loading={false}
        showSocial={true}
      />

      <main>
        <div className="doorloopje-detail-container">
          <button className="back-button" onClick={() => navigate('/')}>
            <ChevronUpIcon className="back-icon" />
            <span>Terug</span>
          </button>

          <h1 className="detail-title">{newsItem.title}</h1>

          {imageUrl && (
            <img src={imageUrl} alt={newsItem.title} className="article-featured-image" />
          )}

          {newsItem.body && Array.isArray(newsItem.body) && newsItem.body.length > 0 && (
            <div style={{ marginTop: '1.5rem' }}>
              <BlocksRenderer content={newsItem.body} />
            </div>
          )}

          {newsItem.auteurs?.length > 0 && (
            <>
              <h3 className="related-title">Auteurs</h3>
              <div className="experts-list">
                {newsItem.auteurs.map(a => (
                  <div key={a.id} className="expert-card">
                    {a.photo?.url ? (
                      <img src={a.photo.url} alt={a.name} />
                    ) : (
                      <div className="avatar-placeholder">{a.name?.charAt(0)}</div>
                    )}
                    <h4>{a.name}</h4>
                    <p>{a.title}</p>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="example-cta-section">
            <div className="example-cta-content">
              <h3>Wil je meer nieuws bekijken?</h3>
              <p>Maak gratis een account. Dan kun je al het nieuws, de doorloopjes, webinars, de assistent en de kennisbank gebruiken.</p>
              <button onClick={handleRegisterClick} className="btn-primary">
                Gratis registreren
              </button>
            </div>
          </div>
        </div>
      </main>

      <Footer
        onLoginClick={handleLoginClick}
        onPrivacyClick={() => setIsPrivacyModalOpen(true)}
        onContactClick={() => setIsContactModalOpen(true)}
      />
    </div>
  );
}

export default NewsItemPublicPage;
